import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import api from '../config/api';
import {
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  LocalActivity as ActivityIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import packageService from '../services/packageService';

const PackageDetail = () => {
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPackage = async () => {
      try {
        setLoading(true);
        
        // Get package data from URL params or fetch from API
        const origin = searchParams.get('origin') || 'LAX';
        const destination = searchParams.get('destination');
        let packageType = searchParams.get('type');
        
        // If type not in URL, try to extract from packageId (format: package_DESTINATION_TYPE_TIMESTAMP)
        if (!packageType && packageId) {
          const parts = packageId.split('_');
          if (parts.length >= 3) {
            // Extract type from packageId (e.g., "package_Cancun_beach_1762028641569" -> "beach")
            packageType = parts[2];
          }
        }
        
        if (destination && packageType) {
          // Get package with real API data for detailed view
          const departureDate = searchParams.get('departure_date') || '2025-11-27';
          const returnDate = searchParams.get('return_date') || '2025-12-04';
          const passengers = searchParams.get('passengers') || '2';
          
          try {
            // Try to get package with real API data first (fresh flights from Duffel)
            console.log('🔄 Fetching fresh package data with live flights...', {
              destination,
              origin,
              departure_date: departureDate,
              return_date: returnDate,
              passengers,
              packageType
            });
            
            const response = await api.get(`/packages/with-apis/${destination}`, {
              params: {
                origin,
                departure_date: departureDate,
                return_date: returnDate,
                passengers,
                packageType
              }
            });
            
            console.log('📦 Package API response:', response.data);
            
            if (response.data && response.data.success) {
              console.log('✅ Got fresh package data with live flights');
              console.log('✈️ Fresh flight offers:', response.data.package.flights?.map(f => ({ id: f.id, price: f.price })));
              setPackageData(response.data.package);
            } else {
              // Fallback to cached data if API fails
              const packages = await packageService.getPopularPackages(origin);
              let foundPackage = packages.find(pkg => pkg.id === packageId);
              if (!foundPackage) {
                foundPackage = packages.find(pkg =>
                  pkg.destination.toLowerCase() === destination.toLowerCase() &&
                  pkg.type === packageType
                );
              }
              
              if (foundPackage) {
                setPackageData(foundPackage);
              } else {
                setError('Package not found');
              }
            }
          } catch (apiError) {
            console.error('❌ Failed to get fresh package data:', apiError);
            console.error('Response:', apiError.response?.data);
            
            // Show error instead of using stale cached data
            setError(
              `Unable to load fresh flight data. ${apiError.response?.data?.error || apiError.message || 'Please try again later.'}`
            );
          }
        } else {
          setError('Invalid package parameters');
        }
        
      } catch (error) {
        console.error('Error loading package:', error);
        setError('Failed to load package details');
      } finally {
        setLoading(false);
      }
    };

    loadPackage();
  }, [packageId, searchParams]);


  const handleBookComponent = (component, componentData) => {
    console.log(`🎯 Booking ${component}:`, componentData);
    console.log('📦 Full package data:', packageData);
    
    if (component === 'flight') {
      // Check if we have fresh flight data with valid offer ID
      if (!componentData || !componentData.id) {
        console.warn('❌ Flight data missing or no ID, cannot book:', componentData);
        alert('Unable to book this flight. The offer may have expired. Please reload the package to get fresh flights.');
        return;
      }
      
      // Navigate to Duffel flight booking page with fresh offer ID
      console.log(`✈️ Navigating to Duffel offer page: /duffel/offer/${componentData.id}`);
      navigate(`/duffel/offer/${componentData.id}?passengers=${packageData.passengers}`, {
        state: {
          flight: componentData,
          origin: packageData.origin,
          destination: packageData.destination,
          departure_date: packageData.departure_date,
          return_date: packageData.return_date
        }
      });
    } else if (component === 'hotel') {
      console.log('🏨 Hotel booking:', componentData);
      
      // Open hotel booking URL in new tab
      if (componentData && componentData.booking_url) {
        console.log(`🔗 Opening hotel booking URL: ${componentData.booking_url}`);
        window.open(componentData.booking_url, '_blank');
      } else {
        // Fallback to Booking.com search
        console.log('🔗 Generating Booking.com search URL...');
        const checkInDate = new Date(packageData.departure_date).toISOString().split('T')[0];
        const checkOutDate = new Date(packageData.return_date).toISOString().split('T')[0];
        const bookingUrl = `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(packageData.destination)}&checkin=${checkInDate}&checkout=${checkOutDate}&group_adults=${packageData.passengers}&no_rooms=1`;
        console.log(`🔗 Opening Booking.com URL: ${bookingUrl}`);
        window.open(bookingUrl, '_blank');
      }
    } else if (component === 'activity') {
      console.log('🎭 Activity booking:', componentData);
      
      // Open activity booking URL in new tab
      if (componentData && componentData.url) {
        console.log(`🔗 Opening activity URL: ${componentData.url}`);
        window.open(componentData.url, '_blank');
      } else {
        console.warn('❌ Activity data missing URL');
      }
    }
  };


  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !packageData) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Package not found'}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/deals?tab=packages')}
          variant="outlined"
        >
          Back to Deals
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          component="button" 
          variant="body1" 
          onClick={() => navigate('/deals?tab=packages')}
          sx={{ textDecoration: 'none' }}
        >
          Deals
        </Link>
        <Typography color="text.primary">
          {packageData.destination} Package
        </Typography>
      </Breadcrumbs>

      {/* Package Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/deals?tab=packages')}
          variant="outlined"
          sx={{ mr: 2 }}
        >
          Back
        </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {packageData.destination} Vacation Package
          </Typography>
          <Chip 
            label={packageData.type} 
            color="primary"
            sx={{ textTransform: 'capitalize', ml: 2 }}
          />
        </Box>
        
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {packageData.description}
        </Typography>

        {/* Package Summary */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    {packageData.duration} days • {packageData.passengers} travelers
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  ${packageData.pricing.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Save ${packageData.pricing.savings} with this package
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Package Components */}
      <Grid container spacing={4}>
        {/* Flights */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FlightIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Flights
                </Typography>
              </Box>
              
              {packageData.flights && packageData.flights.length > 0 ? (
                <Box>
                  {packageData.flights.map((flight, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {flight.airline || 'Flight Option'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {flight.origin} → {flight.destination}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'primary.main', mt: 1 }}>
                        {flight.price || `$${packageData.pricing.flights}`}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleBookComponent('flight', flight)}
                        sx={{ mt: 1 }}
                      >
                        Book Flight
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ mb: 1, fontWeight: 600 }}>
                    {packageData.origin} → {packageData.destination}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {packageData.departure_date} to {packageData.return_date} • {packageData.passengers} travelers
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
                    ${packageData.pricing.flights}
                  </Typography>
                  <Button variant="outlined" onClick={() => handleBookComponent('flight', packageData.flights[0])}>Book Flights</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Hotels */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HotelIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Hotels
                </Typography>
              </Box>
              
              {packageData.hotels && packageData.hotels.length > 0 ? (
                <Box>
                  {packageData.hotels.map((hotel, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {hotel.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <StarIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {hotel.rating}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {hotel.address}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ color: 'primary.main', mt: 1 }}>
                        {hotel.price}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleBookComponent('hotel', hotel)}
                        sx={{ mt: 1 }}
                      >
                        Book Hotel
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Hotel recommendations will be provided during booking
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
                    ${packageData.pricing.hotels}
                  </Typography>
                  <Button variant="outlined" onClick={() => handleBookComponent('hotel', packageData.hotels[0])}>Book Hotels</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Activities */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ActivityIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Activities & Experiences
                </Typography>
              </Box>
              
              {packageData.activities && packageData.activities.length > 0 ? (
                <Grid container spacing={2}>
                  {packageData.activities.map((activity, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card 
                        sx={{ 
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            '& .floating-book-button': {
                              opacity: 1,
                              transform: 'translateY(0)'
                            }
                          }
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={activity.image || `https://picsum.photos/400/200?random=60`}
                          alt={activity.title}
                          onError={(e) => {
                            console.log('Image failed to load:', activity.image);
                            e.target.src = `https://picsum.photos/400/200?random=60`;
                          }}
                          sx={{ 
                            objectFit: 'cover',
                            borderRadius: '8px 8px 0 0',
                            imageRendering: 'high-quality',
                            WebkitImageRendering: 'high-quality',
                            MozImageRendering: 'high-quality',
                            msImageRendering: 'high-quality',
                            backgroundColor: '#f5f5f5'
                          }}
                        />
                        
                        {/* Activity Icon Overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: '#ff6600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                          }}
                        >
                          <ActivityIcon sx={{ fontSize: 16 }} />
                        </Box>

                        <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          {/* Rating */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <StarIcon sx={{ color: '#00a651', fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, mr: 0.5, color: '#00a651' }}>
                              {activity.rating?.toFixed(1) || '4.5'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              (reviews)
                            </Typography>
                          </Box>

                          {/* Title */}
                          <Typography 
                            variant="h6" 
                            component="a"
                            href={activity.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              fontWeight: 700, 
                              mb: 1,
                              color: '#1a1a1a',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            {activity.title}
                          </Typography>

                          {/* Features */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                            {activity.duration && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  ⏱️ {typeof activity.duration === 'object' ? 
                                    `${Math.round((activity.duration.variableDurationFromMinutes || 0) / 60)} hours` : 
                                    activity.duration
                                  }
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.8rem',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                ✓ Free Cancellation
                              </Typography>
                            </Box>
                          </Box>

                          {/* Pricing */}
                          {activity.price && (
                            <Box sx={{ 
                              mt: 'auto', 
                              pt: 1, 
                              borderTop: '1px solid #e0e0e0',
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'space-between'
                            }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.8rem'
                                }}
                              >
                                from
                              </Typography>
                              <Typography 
                                variant="h5" 
                                sx={{ 
                                  color: '#1a1a1a',
                                  fontWeight: 700,
                                  fontSize: '1.4rem'
                                }}
                              >
                                {typeof activity.price === 'string' ? activity.price.replace('From ', '') : activity.price}
                              </Typography>
                            </Box>
                          )}

                          {/* Floating Book Button */}
                          <Button
                            className="floating-book-button"
                            onClick={() => handleBookComponent('activity', activity)}
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              right: 16,
                              borderRadius: '50%',
                              width: 56,
                              height: 56,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                              opacity: 0,
                              transform: 'translateY(8px)',
                              transition: 'all 0.3s ease',
                              minWidth: 'auto',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(0,0,0,0.3)'
                              }
                            }}
                          >
                            <ActivityIcon sx={{ fontSize: 24 }} />
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Activity recommendations will be provided during booking
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
                    ${packageData.pricing.activities}
                  </Typography>
                  <Button variant="outlined" onClick={() => handleBookComponent('activity', packageData.activities[0])}>Book Activities</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Package Includes */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Package Includes
          </Typography>
          <List>
            {packageData.included.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Package Highlights */}
      {packageData.highlights && packageData.highlights.length > 0 && (
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Package Highlights
            </Typography>
            <Grid container spacing={2}>
              {packageData.highlights.map((highlight, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ fontSize: 20, color: 'warning.main', mr: 1 }} />
                    <Typography variant="body1">{highlight}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default PackageDetail;
